import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import MapIcon from "@mui/icons-material/Map";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GroupIcon from "@mui/icons-material/Group";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import ReactDOM from "react-dom";
import axios from "axios";
import { KeyUri } from "../key"; 
import { fetchToursCached } from "./toursApi";
import { TourFilterSidebar, locKey, locLabel } from "./TourFilterSidebar";
import { trackEvent } from "../utils/metaPixel";
import SafeImage, { NoImage, pickImage, normalizeImages } from "../Global/SafeTmage";
function getMetaCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

function getFbc() {
  let fbc = getMetaCookie("_fbc");
  if (!fbc) {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid");
    if (fbclid) {
      fbc = `fb.1.${Date.now()}.${fbclid}`;
      document.cookie = `_fbc=${fbc}; path=/; max-age=${90 * 24 * 60 * 60}`;
    }
  }
  return fbc;
}

const WhatsAppGlyph = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="w-6 h-6"><path fill="#25D366" d="M16.04 3C9.41 3 4 8.41 4 15.04c0 2.64.86 5.09 2.33 7.11L4 29l7.07-2.3a12.02 12.02 0 0 0 4.97 1.06C22.6 27.76 28 22.35 28 15.72 28 9.1 22.66 3 16.04 3z" /> <path fill="#FFF" d="M22.3 18.53c-.38-.19-2.24-1.1-2.59-1.23-.35-.13-.6-.19-.86.19-.25.38-.98 1.23-1.2 1.48-.22.25-.44.28-.82.09-.38-.19-1.63-.6-3.1-1.92-1.15-1.03-1.93-2.3-2.16-2.68-.22-.38-.02-.58.17-.77.18-.18.38-.44.57-.66.19-.22.25-.38.38-.63.13-.25.06-.47-.03-.66-.09-.19-.86-2.07-1.18-2.84-.31-.75-.63-.65-.86-.66h-.73c-.25 0-.66.09-1 .44-.35.38-1.32 1.29-1.32 3.15 0 1.85 1.35 3.64 1.54 3.89.19.25 2.65 4.05 6.42 5.68 3.77 1.63 3.77 1.09 4.45 1.02.68-.06 2.24-.91 2.56-1.79.31-.88.31-1.63.22-1.79-.09-.16-.35-.25-.73-.44z" /></svg>
);

const TourImageCarousel = React.memo(({ images, title }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [interacted, setInteracted] = useState(false);
  const allImages = React.useMemo(() => normalizeImages(images), [images]);
  const hasMultipleImages = allImages.length > 1;

  const nextImage = (e) => {
    e.preventDefault(); e.stopPropagation();
    setInteracted(true);
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };
  const prevImage = (e) => {
    e.preventDefault(); e.stopPropagation();
    setInteracted(true);
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };
  const goToImage = (idx, e) => {
    e.preventDefault(); e.stopPropagation();
    setInteracted(true);
    setCurrentImageIndex(idx);
  };

  if (allImages.length === 0) {
    return (
      <div className="relative h-[200px] overflow-hidden">
        <NoImage type="tour" />
      </div>
    );
  }

  return (
    <div className="relative h-[200px] overflow-hidden bg-blue-50">
      <div
        className="flex transition-transform duration-500 ease-out h-full"
        style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
      >
        {allImages.map((img, idx) => (
          <div key={idx} className="w-full h-full flex-shrink-0">
            {/* only mount the current + adjacent slides; the rest are empty placeholders */}
            {(idx === currentImageIndex || (interacted && Math.abs(idx - currentImageIndex) <= 1)) && (
              <SafeImage
                src={img} width={520} loading="lazy" type="tour" alt={`${title} - ${idx + 1}`}
                
              />
            )}
          </div>
        ))}
      </div>

      {hasMultipleImages && currentImageIndex > 0 && (
        <button
          onClick={prevImage}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white shadow-lg flex items-center justify-center rounded-full transition-all duration-300 z-20 hover:scale-110"
        >
          <svg className="w-4 h-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {hasMultipleImages && currentImageIndex < allImages.length - 1 && (
        <button
          onClick={nextImage}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white shadow-lg flex items-center justify-center rounded-full transition-all duration-300 z-20 hover:scale-110"
        >
          <svg className="w-4 h-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {hasMultipleImages && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
          {allImages.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => goToImage(idx, e)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                idx === currentImageIndex ? 'bg-white shadow-lg w-4' : 'bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
    </div>
  );
});


const getEffectiveTourPrice = (tour, hiddenChargesPerPerson = 0) => {
  const base =
    Number(tour.display_price || tour.price || 0) + hiddenChargesPerPerson;

  // 1) HOTELS: per stop, default hotel (else cheapest), cheapest room x that hotel's nights.
  //    Full room cost, no division by guests.
  let hotelTotal = 0;
  if (tour.stay_policy !== 'not_available' && Array.isArray(tour.hotels)) {
    const stops = {};
    tour.hotels.forEach((h, i) => {
      const label = (h.location_label || '').trim().toLowerCase();
      const key = label || `__blank_${i}`;
      (stops[key] = stops[key] || []).push(h);
    });

    Object.values(stops).forEach((stopHotels) => {
      const defaults = stopHotels.filter((h) => h.is_default);
      const pool = defaults.length ? defaults : stopHotels;

      let best = null;
      pool.forEach((h) => {
        const rooms =
          h.property_type === 'existing'
            ? h.selected_rooms || []
            : [{ price_per_night: h.price_per_night || 0 }];
        const nights = Number(h.nights) || 1;

        rooms.forEach((r) => {
          const p = Number(r.price_per_night || 0);
          if (!best || p < best.p) best = { p, nights };
        });
      });

      if (best) hotelTotal += best.p * best.nights;
    });
  }

  // 2) TRANSPORT: private = per vehicle, shared/flight = per person (1 adult)
  let transportTotal = 0;
  if (
    tour.travel_policy !== 'not_required' &&
    Array.isArray(tour.transport) &&
    tour.transport.length > 0
  ) {
    const unit = (t) =>
      t.type === 'private'
        ? Number(t.price_per_vehicle || 0)
        : Number(t.price_per_person || 0);

    const cheapest = [...tour.transport].sort((a, b) => unit(a) - unit(b))[0];
    if (cheapest) {
      transportTotal = unit(cheapest) * (Number(cheapest.usage_count) || 1);
    }
  }

  // 3) SIGHTSEEING: every priced item, per person
  let sightTotal = 0;
  (tour.itinerary || []).forEach((d) =>
    (d.sightseeing || []).forEach((s) => {
      if (Number(s.price) > 0) sightTotal += Number(s.price);
    }),
  );

  // 4) No division by guests
  return base + hotelTotal + transportTotal + sightTotal;
};


const openWhatsApp = (tour) => {
  const msg = encodeURIComponent(
    `Hello,\n\n` +
    `I am interested in the following tour package:\n` +
    `• Tour: ${tour.title}\n` +
    `• Destination: ${tour.location}\n\n` +
    `Could you please confirm the available dates, current offers (if any), and the booking process for this package?\n\n` +
    `Thank you.`
  );
  window.open(`https://wa.me/919883587965?text=${msg}`, "_blank");
};

// Memoized: re-renders only when its own tour / price changes, NOT on every
// filter, slider or modal keystroke in the parent.
const TourCard = React.memo(({ tour, tourId, price, onCallback }) => {
  const navigate = useNavigate();
  const orig = Number(tour.original_price || 0);
  return (
    <Link
      to={`/tours/${tourId}`}
      className="tour-card flex flex-col rounded-2xl overflow-hidden bg-white border border-blue-200 border-b-4 border-b-blue-600 shadow-md lg:hover:shadow-xl transition-shadow duration-200"
      // Off-screen cards skip layout/paint entirely until they scroll near the viewport
      style={{ contentVisibility: "auto", containIntrinsicSize: "auto 440px" }}
    >
      <TourImageCarousel images={tour.image_url} title={tour.title} />

      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-xl font-semibold mb-2 text-[#0B2A5B]">{tour.title}</h3>

        <div className="space-y-1 text-sm text-gray-600 mb-4">
          <div className="flex items-center gap-2">
            <MapIcon fontSize="small" color="primary" />
            <span>{tour.location}</span>
          </div>
          <div className="flex items-center gap-2">
            <CalendarTodayIcon fontSize="small" color="primary" />
            <span>{tour.duration}</span>
          </div>
          {tour.available_slots != null && (
            <div className="flex items-center gap-2">
              <GroupIcon fontSize="small" color="primary" />
              <span>{tour.available_slots} slots available</span>
            </div>
          )}
        </div>

        <div className="mt-auto">
          <div className="flex justify-between items-end mb-3">
            <div className="flex flex-col items-start gap-1 flex-1">
              {orig > price && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400 line-through">₹{orig.toLocaleString("en-IN")}</span>
                  <span className="text-[11px] font-semibold px-2 py-[2px] rounded-sm bg-green-100 text-green-700 border border-green-200 uppercase tracking-wide">
                    SAVE ₹{(orig - price).toLocaleString("en-IN")}
                  </span>
                </div>
              )}
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-semibold text-gray-900">₹{price.toLocaleString("en-IN")}</span>
                <span className="text-xs font-medium text-gray-600">/Adult</span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              aria-label="Chat on WhatsApp"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); openWhatsApp(tour); }}
              className="flex items-center justify-center border border-green-500 text-green-600 rounded-lg w-14 h-10 flex-none hover:bg-green-50"
            >
              <WhatsAppGlyph />
            </button>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCallback(tour); }}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg py-2 text-xs font-semibold"
            >
              Request Callback
            </button>
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/tours/${tourId}`); }}
              className="flex-1 flex items-center justify-center border border-blue-400 text-gray-700 rounded-lg py-2 text-xs font-semibold hover:bg-gray-50 cursor-pointer"
            >
              View Details
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
});

// Debounce a fast-changing value (price slider) so filtering 100s of tours
// doesn't run on every drag tick.
const useDebounced = (value, ms = 250) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

const getTourType = (t) => t.tour_type || (t.country ? 'international' : 'domestic');

const Tours = ({ selectedCategory, selectedSort, searchTerm, selectedState, tripType, destinationType, onTripTypeChange }) => {
   const [searchParams, setSearchParams] = useSearchParams();
   const navigate = useNavigate();
  const [allTours, setAllTours] = useState([]); 
  const [locationFilter, setLocationFilter] = useState("");
  const [loading, setLoading] = useState(true); // true from the start: avoids a flash of "No tours found"

 
  const [hiddenChargesPerPerson, setHiddenChargesPerPerson] = useState(0);

  
  // Price per tour is computed ONCE per (tours, charges) instead of on every
  // filter / sort comparison / render.
  const priceMap = useMemo(() => {
    const m = new Map();
    allTours.forEach((t) => m.set(t, getEffectiveTourPrice(t, hiddenChargesPerPerson)));
    return m;
  }, [allTours, hiddenChargesPerPerson]);
  const priceOf = useCallback(
    (tour) => priceMap.get(tour) ?? getEffectiveTourPrice(tour, hiddenChargesPerPerson),
    [priceMap, hiddenChargesPerPerson]
  );

 //const [selectedState, setSelectedState] = useState(""); 

    // request‑callback modal state
 const [callbackOpen, setCallbackOpen] = useState(false);
const [callbackTour, setCallbackTour] = useState(null);
const [cbErrors, setCbErrors] = useState({});
const [cbName, setCbName] = useState("");
const [cbPhone, setCbPhone] = useState("");
const [cbEmail, setCbEmail] = useState("");
const [cbTravelDate, setCbTravelDate] = useState("");
const [cbTravellerCount, setCbTravellerCount] = useState(1);
const [cbMessage, setCbMessage] = useState("");
const [cbSuccess, setCbSuccess] = useState(false);

  // Sidebar filter states
  const [priceRange, setPriceRange] = useState([1000, 50000]);
  const [sidebarCategories, setSidebarCategories] = useState(['all']);
  const [selectedRating, setSelectedRating] = useState(null);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
const [selectedDuration, setSelectedDuration] = useState(null);
const [selectedPackageTypes, setSelectedPackageTypes] = useState([]);
const [selectedLocations, setSelectedLocations] = useState([]);

// Fetch TourPricing config for hidden platform/additional charges (mirrors TourDetail.jsx)
useEffect(() => {
  const fetchTourPricingConfig = async () => {
    try {
      const res = await axios.get(`${KeyUri.BACKEND_URI}/tourpricing`);
      const pricing = res.data?.data;
      const platformFee = Number(pricing?.platform_price || 0);
      const misc = Number(pricing?.miscellaneous_charges || 0);
      const addlOne = Number(pricing?.additional_charges_one || 0);
      const addlTwo = Number(pricing?.additional_charges_two || 0);
      setHiddenChargesPerPerson(platformFee + misc + addlOne + addlTwo);
    } catch (err) {
      console.error("Failed to fetch tour pricing config:", err);
    }
  };
  fetchTourPricingConfig();
}, []);

useEffect(() => {
  const category = searchParams.get("category");
  const minPrice = searchParams.get("minPrice");
  const maxPriceParam = searchParams.get("maxPrice");
  const rating = searchParams.get("rating");
  const duration = searchParams.get("duration");
  const packageType = searchParams.get("packageType"); 
  const locations = searchParams.get("locations");
  const trip = searchParams.get("tripType"); 
  // Category
  if (category) {
    setSidebarCategories(category.split(","));
  }

  // Price
  if (minPrice || maxPriceParam) {
    setPriceRange([
      Number(minPrice) || 1000,
      Number(maxPriceParam) || 50000,
    ]);
  }

  // Rating
  if (rating) {
    setSelectedRating(Number(rating));
  }

  // Duration
  if (duration) {
    setSelectedDuration(duration);
  }

    if (packageType) setSelectedPackageTypes(packageType.split(","));   
  if (locations) setSelectedLocations([...new Map(locations.split(",").filter(Boolean).map((l) => [locKey(l), locLabel(l)])).values()]); 
 if (trip) onTripTypeChange(trip); 
}, []);

useEffect(() => {
  if (tripType === 'group') {
    setSelectedPackageTypes(['group']);
  } else if (tripType === 'private') {        
    setSelectedPackageTypes(['private']);
  } else {
    setSelectedPackageTypes([]);
  }
}, [tripType]);

{/*}
useEffect(() => {
   
  // Priority 1: Pill selectedCategory (from FilterSection)
  if (selectedCategory && selectedCategory !== 'all') {
    //  console.log(" PILL SYNC:", selectedCategory);
    setSidebarCategories([selectedCategory]);
    return;
  }
  
  // Priority 2: URL param (from Home page)
  const urlCategory = searchParams.get('category');
  if (urlCategory && urlCategory !== 'all') {
   //  console.log(" URL SYNC:", urlCategory);
    setSidebarCategories([urlCategory.toLowerCase()]);
    return;
  }
  
  // Priority 3: Default
 // console.log(" DEFAULT: all");
  setSidebarCategories(['all']);
}, [selectedCategory, searchParams]); // Both triggers

*/}

useEffect(() => {
  if (selectedCategory && selectedCategory !== "all") {
    setSidebarCategories([selectedCategory]);
  } else if (selectedCategory === "all") {
    setSidebarCategories(["all"]);
  }
}, [selectedCategory]);



const handlePackageTypeChange = (key) => {
  setSelectedPackageTypes((prev) => {
    const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
    return next;
  });

  if (key === 'group') {
    onTripTypeChange(selectedPackageTypes.includes('group') ? '' : 'group');
  } else if (key === 'private') {
    onTripTypeChange(selectedPackageTypes.includes('private') ? '' : 'private');   // ← was 'bespoke'
  }
};


// Derive unique locations from allTours
const availableLocations = useMemo(() => {
  // one entry per place: "manali", "Manali ", "MANALI" all become "Manali"
  const unique = new Map();
  allTours.forEach((t) => {
    const k = locKey(t.location);
    if (k && !unique.has(k)) unique.set(k, locLabel(t.location));
  });
  return [...unique.values()].sort();
}, [allTours]);

const handleLocationChange = (loc) => {
  const k = locKey(loc);
  setSelectedLocations((prev) =>
    prev.some((l) => locKey(l) === k)
      ? prev.filter((l) => locKey(l) !== k)
      : [...prev, locLabel(loc)]
  );
};

  // Filter handlers
  const handleCategoryChange = (categoryKey) => {
    if (categoryKey === 'all') {
      setSidebarCategories(['all']);
    } else {
      setSidebarCategories((prev) => {
        const filtered = prev.filter((c) => c !== 'all');
        if (filtered.includes(categoryKey)) {
          const newCategories = filtered.filter((c) => c !== categoryKey);
          return newCategories.length === 0 ? ['all'] : newCategories;
        }
        return [...filtered, categoryKey];
      });
    }
  };
 // Calculate max price from tours
const maxPrice = useMemo(() => {
  if (allTours.length === 0) return 50000;
  const max = Math.max(...allTours.map((t) => priceOf(t)));
  return Math.ceil(max / 1000) * 1000 || 50000;
}, [allTours, priceOf]);

useEffect(() => {
  const timeout = setTimeout(() => {
    const params = {};
    if (!sidebarCategories.includes("all") && sidebarCategories.length > 0) params.category = sidebarCategories.join(",");
    if (priceRange[0] !== 1000 || priceRange[1] !== maxPrice) {
      params.minPrice = priceRange[0];
      params.maxPrice = priceRange[1];
    }
    if (selectedRating) params.rating = selectedRating;
    if (selectedDuration) params.duration = selectedDuration;
    if (selectedState) params.state = selectedState;
    if (searchTerm?.trim()) params.search = searchTerm.trim();
    if (selectedPackageTypes.length > 0) params.packageType = selectedPackageTypes.join(",");
    if (selectedLocations.length > 0) params.locations = selectedLocations.join(",");
        if (tripType) params.tripType = tripType;
    if (destinationType) params.destination = destinationType;

    const next = new URLSearchParams(params).toString();
    if (next !== searchParams.toString()) setSearchParams(params, { replace: true });
  }, 400);

  return () => clearTimeout(timeout);
}, [sidebarCategories, priceRange, maxPrice, selectedRating, locationFilter, selectedState, searchTerm, selectedDuration, selectedPackageTypes, selectedLocations, tripType, destinationType]);
 // Reset filters when searchTerm changes
useEffect(() => {
  if (!searchTerm) {
    setLocationFilter("");
    //setSidebarCategories(["all"]);
    setSelectedRating(null);
    setPriceRange([1000, maxPrice]);
  }
}, [searchTerm, maxPrice]); 


const handleClearAllFilters = () => {
  setPriceRange([1000, maxPrice]);
  setSidebarCategories(['all']);
  setSelectedRating(null);
  setLocationFilter("");
  setSelectedDuration(null);
  setSelectedPackageTypes([]);
  setSelectedLocations([]);
  onTripTypeChange(''); 
};


 

  // 1) Fetch tours from backend once
  useEffect(() => {
    const fetchTours = async () => {
      try {
        setLoading(true);
        const data = await fetchToursCached();
        setAllTours(data);
      } catch (err) {
        console.error("Error fetching tours:", err);
        setAllTours([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
  }, []);

  
  // 2) Apply search, location, category, sort on client
  const debouncedPriceRange = useDebounced(priceRange, 250);
  const tours = useMemo(() => {

    let filtered = [...allTours];

  filtered = filtered.filter((t) => t.available === true);

if (searchTerm && searchTerm.trim() !== "") {
  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const q = norm(searchTerm);

  filtered = filtered.filter((t) =>
    [
      t.title,
      t.location,
      t.stateKey,
      t.country,
      t.category,
            getTourType(t) === "domestic" ? "india" : "",
    ].some((f) => norm(f).includes(q))
  );
}



    if (locationFilter) {
      const lowerLocation = locationFilter.toLowerCase();
      filtered = filtered.filter((t) =>
        t.location?.toLowerCase().includes(lowerLocation)
      );
    }

    if (selectedState) {
      filtered = filtered.filter(
        (t) => t.stateKey === selectedState
      );
    }

  if (tripType === 'group') {
  filtered = filtered.filter((t) => t.is_group_tour === true);
} else if (tripType === 'private') {          // ← was 'bespoke'
  filtered = filtered.filter((t) => t.is_group_tour !== true); // false or undefined = private
}

       if (destinationType === 'domestic') {
      filtered = filtered.filter((t) => getTourType(t) === 'domestic');
    } else if (destinationType === 'international') {
      filtered = filtered.filter((t) => getTourType(t) === 'international');
    }

  const activeFilters = [];
if (selectedCategory && selectedCategory !== 'all') activeFilters.push(selectedCategory);
if (!sidebarCategories.includes('all') && sidebarCategories.length > 0) {
  activeFilters.push(...sidebarCategories);
}

if (activeFilters.length > 0) {
 // console.log(" APPLYING FILTERS:", activeFilters);
  filtered = filtered.filter((t) => activeFilters.includes(t.category));
}

    
   // Price range filter
filtered = filtered.filter((t) => {
  const price = priceOf(t);
  return price >= debouncedPriceRange[0] && price <= debouncedPriceRange[1];
});

    // Rating filter
    if (selectedRating) {
      filtered = filtered.filter((t) => Number(t.rating || 0) >= selectedRating);
    }

    // Duration filter
if (selectedDuration) {
  const durations = [
    { key: '1-3', min: 1, max: 3 },
    { key: '4-7', min: 4, max: 7 },
    { key: '8-10', min: 8, max: 10 },
    { key: '10+', min: 11, max: Infinity },
  ];

  const selected = durations.find(d => d.key === selectedDuration);

  if (selected) {
    filtered = filtered.filter((t) => {
      const days =
        Number(t.days) || // if backend gives days
        Number(t.duration?.match(/\d+/)?.[0]) || // fallback: extract from "5 days"
        0;

      return days >= selected.min && days <= selected.max;
    });
  }
}

 // Package type filter
if (selectedPackageTypes.length > 0) {
  filtered = filtered.filter((t) => {
    const isGroup = t.is_group_tour === true; // undefined/null → false → private

    if (selectedPackageTypes.includes('group') && selectedPackageTypes.includes('private')) {
      return true;
    }
    if (selectedPackageTypes.includes('group')) return isGroup === true;
    if (selectedPackageTypes.includes('private')) return isGroup === false;
    return true;
  });
}

// Location filter
if (selectedLocations.length > 0) {
  const selKeys = selectedLocations.map(locKey);
  filtered = filtered.filter((t) => selKeys.includes(locKey(t.location)));
}
    // Sorting
   if (selectedSort === "price-low") {
  filtered = filtered.sort(
    (a, b) => priceOf(a) - priceOf(b)
  );
} else if (selectedSort === "price-high") {
  filtered = filtered.sort(
    (a, b) => priceOf(b) - priceOf(a)
  );
} else if (selectedSort === "rating") {
      filtered = filtered.sort(
        (a, b) => Number(b.rating || 0) - Number(a.rating || 0)
      );
    }

    return filtered;
    }, [allTours, searchTerm, locationFilter, selectedCategory, selectedSort, selectedState, sidebarCategories, debouncedPriceRange, selectedRating, selectedDuration, selectedPackageTypes, selectedLocations, tripType, destinationType, priceOf]);
  
  // Render cards progressively (9 at a time) instead of mounting the whole catalogue at once.
  const PAGE_SIZE = 9;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [tours]);
  const hasMore = visibleCount < tours.length;
  const visibleTours = useMemo(() => tours.slice(0, visibleCount), [tours, visibleCount]);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setVisibleCount((c) => c + PAGE_SIZE); },
      { rootMargin: "700px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, visibleCount]);

  const groupedTours = useMemo(() => {
  const groups = {};
  tours.forEach((tour) => {
    const category = tour.category || "other";
    if (!groups[category]) groups[category] = [];
    groups[category].push(tour);
  });
  return groups;
}, [tours]);

  const openCallbackModal = useCallback((tour) => {
  setCallbackTour(tour);
  setCallbackOpen(true);
}, []);

const closeCallbackModal = () => {
  setCallbackOpen(false);
  setCallbackTour(null);
  setCbName("");
  setCbPhone("");
  setCbEmail("");
  setCbTravelDate("");
  setCbTravellerCount(1);
  setCbMessage("");
  setCbSuccess(false);
};
const validateCallbackForm = () => {
  const errors = {};

  // Name validation
  if (!cbName.trim()) {
    errors.name = "Full name is required";
  } else if (cbName.trim().length < 3) {
    errors.name = "Name must be at least 3 characters";
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!cbEmail.trim()) {
    errors.email = "Email is required";
  } else if (!emailRegex.test(cbEmail)) {
    errors.email = "Enter a valid email address";
  }

  // Phone validation (Indian 10 digit)
  const phoneRegex = /^[6-9]\d{9}$/;
  if (!cbPhone.trim()) {
    errors.phone = "Phone number is required";
  } else if (!phoneRegex.test(cbPhone)) {
    errors.phone = "Enter valid 10-digit mobile number";
  }

  // Travel Date validation
  if (!cbTravelDate) {
    errors.travelDate = "Travel date is required";
  } else {
    const selectedDate = new Date(cbTravelDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      errors.travelDate = "Travel date cannot be in the past";
    }
  }

  // Traveller count
  if (!cbTravellerCount || cbTravellerCount < 1) {
    errors.travellerCount = "At least 1 traveller required";
  }

  setCbErrors(errors);
  return Object.keys(errors).length === 0;
};

const handleCallbackSubmit = async (e) => {
  e.preventDefault();

  if (!validateCallbackForm()) return;

  try {
    const response = await fetch(`${KeyUri.BACKEND_URI}/leads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: cbName,
        phone: cbPhone,
        email: cbEmail,
        tour: callbackTour?.title,
        travelDate: cbTravelDate,
        travellerCount: cbTravellerCount,
        message: cbMessage,
        fbp: getMetaCookie("_fbp"),   
        fbc: getFbc(),  
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to submit lead");
    }

     const data = await response.json();

    trackEvent("Lead", {
      content_name: "tour_callback_request",
      content_category: callbackTour?.title || "tour",
      value: 0,
      currency: "INR",
    },
  data.eventId
);

    alert("Thank you! Our expert will call you shortly.");
    closeCallbackModal();

  } catch (error) {
    alert(error.message);
  }
};


const activeFilterCount = useMemo(() => {
  let count = 0;
  if (!sidebarCategories.includes("all")) count++;
  if (priceRange[0] !== 1000 || priceRange[1] !== maxPrice) count++;
  if (selectedRating) count++;
  if (locationFilter) count++;
  if (selectedDuration) count++;
  if (selectedState) count++;
  if (selectedPackageTypes.length > 0) count++;
  if (selectedLocations.length > 0) count++;
  if (searchTerm && searchTerm.trim() !== "") count++;
    if (tripType) count++;
  if (destinationType) count++;

  return count;
}, [sidebarCategories, priceRange, maxPrice, selectedRating, locationFilter, selectedState, searchTerm, selectedDuration, selectedPackageTypes, selectedLocations, tripType, destinationType]);
return (
    <div className="min-h-screen px-4 sm:px-6 pt-6 pb-20 max-w-7xl mx-auto">
      {/* Mobile Filter Button */}
     {/*} <div className="lg:hidden mb-4">
        <button
          onClick={() => setShowMobileFilter(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
        >
          <TuneIcon fontSize="small" className="text-blue-600" />
          <span className="text-sm font-medium text-gray-700">Filters</span>
        </button>
      </div>*/}

      {/* Mobile Filter Overlay */}
{/* Mobile Filter Overlay */}
{showMobileFilter && ReactDOM.createPortal(
  <div className="fixed inset-0 z-[999998] lg:hidden">
    <div
      className="absolute inset-0 bg-black/50"
      onClick={() => setShowMobileFilter(false)}
    />
    <div className="absolute left-0 top-0 h-full w-80 max-w-[85vw] bg-white overflow-y-auto">
      <div className="flex items-center justify-between p-4 border-b">
        <span className="font-semibold text-gray-800">Filters</span>
        <button
          onClick={() => setShowMobileFilter(false)}
          className="p-1 hover:bg-gray-100 rounded-full"
        >
          <CloseIcon />
        </button>
      </div>
      <div className="p-4">
        <TourFilterSidebar
          priceRange={priceRange}
          onPriceChange={setPriceRange}
          maxPrice={maxPrice}
          selectedCategories={sidebarCategories}
          onCategoryChange={handleCategoryChange}
          selectedRating={selectedRating}
          onRatingChange={setSelectedRating}
          selectedDuration={selectedDuration}
          onDurationChange={setSelectedDuration}
          onClearAll={handleClearAllFilters}
          selectedPackageTypes={selectedPackageTypes}
          onPackageTypeChange={handlePackageTypeChange}
          selectedLocations={selectedLocations}
          onLocationChange={handleLocationChange}
          availableLocations={availableLocations}
        />
      </div>
    </div>
  </div>,
  document.body
)}

      {/* Tours Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 py-6">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-[430px] rounded-2xl bg-slate-100 animate-pulse" />)}</div>
      ) : (
        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <TourFilterSidebar
              priceRange={priceRange}
              onPriceChange={setPriceRange}
              maxPrice={maxPrice}
              selectedCategories={sidebarCategories}
              onCategoryChange={handleCategoryChange}
              selectedRating={selectedRating}
              onRatingChange={setSelectedRating}
               selectedDuration={selectedDuration}
  onDurationChange={setSelectedDuration}
              onClearAll={handleClearAllFilters}
              selectedPackageTypes={selectedPackageTypes}
onPackageTypeChange={handlePackageTypeChange}
selectedLocations={selectedLocations}
onLocationChange={handleLocationChange}
availableLocations={availableLocations}
            />
          </div>

          {/* Tour Cards */}
          <div className="flex-1 min-w-0">
            {tours.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <div className="text-6xl mb-4">🔍</div>
   <p className="text-lg font-medium mb-2">
  {destinationType === 'international' && !allTours.some((t) => getTourType(t) === 'international')
    ? 'International packages are coming soon'
    : 'No tours found'}
</p>
                <p className="text-sm">Try adjusting your filters or clearing them to see more results.</p>
               {/*} <button
                  onClick={handleClearAllFilters}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Clear All Filters
                </button>*/}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {visibleTours.map((tour) => {
                  const tourId = tour._id || tour.id;
                  return (
                    <TourCard
                      key={tourId}
                      tour={tour}
                      tourId={tourId}
                      price={priceOf(tour)}
                      onCallback={openCallbackModal}
                    />
                  );
                })}
              </div>
            )}
            {hasMore && <div ref={sentinelRef} className="h-16 flex items-center justify-center text-sm text-slate-400">Loading more tours…</div>}
          </div>
        </div>
      )}
{callbackOpen && (
  <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40">
    {/* PANEL */}
    <div className=" relative bg-white w-full md:max-w-xl max-h-[90vh] rounded-t-2xl md:rounded-2xl shadow-2xl overflow-y-auto">
      {/* Top red strip */}
     

      {/* Close button (top‑right) */}
      <button
        onClick={closeCallbackModal}
        className="absolute right-3 top-3 text-gray-500 md:text-gray-500 md:hover:text-gray-700 text-2xl font-bold leading-none"
      >
        ×
      </button>

      {/* Tour header row */}
      <div className="px-4 pt-4 pb-3 flex items-start gap-3 border-b border-gray-100">
      <div className="w-14 h-14 rounded-md overflow-hidden flex-shrink-0">
  <SafeImage src={pickImage(callbackTour?.image_url)} width={120} type="tour" alt={callbackTour?.title} compact />
</div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-800 line-clamp-2">
            {callbackTour?.title}
          </p>

          {/* price row like listing */}
     <div className="flex items-center gap-2 mt-1">
  <span className="text-base font-bold text-gray-900">
    ₹{priceOf(callbackTour || {}).toLocaleString("en-IN")}
  </span>
  {callbackTour?.original_price &&
    callbackTour.original_price > priceOf(callbackTour) && (
      <>
        <span className="text-xs text-gray-400 line-through">
          ₹{Number(callbackTour.original_price).toLocaleString("en-IN")}
        </span>
        <span className="text-[10px] font-semibold px-2 py-[1px] rounded-sm bg-green-100 text-green-700 border border-green-200 uppercase tracking-wide">
          SAVE ₹
          {(
            Number(callbackTour.original_price) - priceOf(callbackTour)
          ).toLocaleString("en-IN")}
        </span>
      </>
    )}
</div>
        </div>
      </div>

      {/* FORM BODY */}
      <form onSubmit={handleCallbackSubmit} className="px-4 pb-4 pt-2 space-y-3">
        {/* Full name */}
 <TextField
  label="Full Name"
  fullWidth
  required
  size="small"
  value={cbName}
  onChange={(e) => setCbName(e.target.value)}
  error={!!cbErrors.name}
  helperText={cbErrors.name}
/>

        {/* Email */}
      <TextField
  label="Email"
  type="email"
  fullWidth
  required
  size="small"
  value={cbEmail}
  onChange={(e) => setCbEmail(e.target.value)}
  error={!!cbErrors.email}
  helperText={cbErrors.email}
/>

        {/* Phone row: country code + phone */}
        <div className="grid grid-cols-4 gap-2">
          <TextField
            select
            value="+91"
            size="small"
            disabled
            fullWidth
          >
            <option value="+91">+91</option>
          </TextField>

        <TextField
  label="Your Phone"
  type="tel"
  fullWidth
  required
  size="small"
  value={cbPhone}
  onChange={(e) => setCbPhone(e.target.value.replace(/\D/g, ""))}
  error={!!cbErrors.phone}
  helperText={cbErrors.phone}
  inputProps={{ maxLength: 10 }}
  className="col-span-3"
/>
        </div>

        {/* Travel date + traveller count */}
        <div className="grid grid-cols-2 gap-2">
         <TextField
  label="Travel Date"
  type="date"
  fullWidth
  required
  size="small"
  InputLabelProps={{ shrink: true }}
  value={cbTravelDate}
  onChange={(e) => setCbTravelDate(e.target.value)}
  error={!!cbErrors.travelDate}
  helperText={cbErrors.travelDate}
/>
        <TextField
  label="Traveller Count"
  type="number"
  fullWidth
  required
  size="small"
  inputProps={{ min: 1 }}
  value={cbTravellerCount}
  onChange={(e) => setCbTravellerCount(e.target.value)}
  error={!!cbErrors.travellerCount}
  helperText={cbErrors.travellerCount}
/>
        </div>

        {/* Message */}
        <TextField
          label="Message"
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          size="small"
          value={cbMessage}
          onChange={(e) => setCbMessage(e.target.value)}
          placeholder="Share your plans, questions, or customization needs..."
        />

        {/* Bottom CTA button */}
        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{
            mt: 1,
            mb: 1,
            backgroundColor: "#2563eb",
            "&:hover": { backgroundColor: "#1d4ed8" },
            py: 1.5,
          }}
        >
          Connect with an Expert
        </Button>
      </form>
    </div>
  </div>
)}
{/* Floating Mobile Filter Button */}
{ReactDOM.createPortal(
  <button
    onClick={() => setShowMobileFilter(true)}
    className="fixed right-5 bottom-64 z-[999999] flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full shadow-2xl hover:bg-blue-700 active:scale-95 transition-all lg:hidden"
  >
    <TuneIcon />
    {activeFilterCount > 0 && (
      <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full">
        {activeFilterCount}
      </span>
    )}
  </button>,
  document.body
)}




    </div>
  );
};

export default Tours;